"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FINANCIAL_DOCS_INSTRUCTIONS = exports.calculateRequiredIncomeDocsFromIncomeTypes = exports.UNDERWRITING_REQUIREMENTS = exports.MIN_DOCS = exports.RENTAL_APP_INCOME_DOC_TYPES = exports.APPLICANT_TYPES = void 0;
const documents_1 = require("./documents");
const pluralize_1 = __importDefault(require("pluralize"));
const number_to_words_1 = __importDefault(require("number-to-words"));
/**
 * should more accurately be called EMPLOYMENT_STATUSES
 */
exports.APPLICANT_TYPES = Object.freeze({
    EMPLOYED: 'employed',
    NEWLY_EMPLOYED: 'newly_employed',
    SELF_EMPLOYED: 'self_employed',
    SOCIAL_SECURITY: 'social_security',
    NOT_EMPLOYED: 'not_employed'
});
const { BANK_STATEMENT, PAY_STUB, TAX_RETURN, OFFER_LETTER, SOCIAL_SECURITY_AWARD_LETTER, ARTICLES_OF_ORGANIZATION } = documents_1.DOCUMENT_TYPES;
exports.RENTAL_APP_INCOME_DOC_TYPES = Object.freeze({
    BANK_STATEMENT,
    PAY_STUB,
    TAX_RETURN,
    OFFER_LETTER,
    SOCIAL_SECURITY_AWARD_LETTER,
    ARTICLES_OF_ORGANIZATION
});
exports.MIN_DOCS = Object.freeze({
    MIN_BANK_STATEMENTS_FOR_SELF_EMPLOYED: 3,
    MIN_BANK_STATEMENTS_FOR_EMPLOYED: 2,
    MIN_BANK_STATEMENTS_FOR_SOCIAL_SECURITY: 2
});
exports.UNDERWRITING_REQUIREMENTS = Object.freeze({
    [exports.APPLICANT_TYPES.EMPLOYED]: [
        { docType: exports.RENTAL_APP_INCOME_DOC_TYPES.PAY_STUB },
        {
            docType: exports.RENTAL_APP_INCOME_DOC_TYPES.BANK_STATEMENT,
            min: exports.MIN_DOCS.MIN_BANK_STATEMENTS_FOR_EMPLOYED
        }
    ],
    [exports.APPLICANT_TYPES.NEWLY_EMPLOYED]: [
        { docType: exports.RENTAL_APP_INCOME_DOC_TYPES.OFFER_LETTER },
        {
            docType: exports.RENTAL_APP_INCOME_DOC_TYPES.BANK_STATEMENT,
            min: exports.MIN_DOCS.MIN_BANK_STATEMENTS_FOR_EMPLOYED
        }
    ],
    [exports.APPLICANT_TYPES.SELF_EMPLOYED]: [
        { docType: exports.RENTAL_APP_INCOME_DOC_TYPES.TAX_RETURN },
        {
            docType: exports.RENTAL_APP_INCOME_DOC_TYPES.BANK_STATEMENT,
            min: exports.MIN_DOCS.MIN_BANK_STATEMENTS_FOR_SELF_EMPLOYED
        },
        { docType: exports.RENTAL_APP_INCOME_DOC_TYPES.ARTICLES_OF_ORGANIZATION }
    ],
    [exports.APPLICANT_TYPES.SOCIAL_SECURITY]: [
        { docType: exports.RENTAL_APP_INCOME_DOC_TYPES.SOCIAL_SECURITY_AWARD_LETTER },
        {
            docType: exports.RENTAL_APP_INCOME_DOC_TYPES.BANK_STATEMENT,
            min: exports.MIN_DOCS.MIN_BANK_STATEMENTS_FOR_SOCIAL_SECURITY
        }
    ],
    [exports.APPLICANT_TYPES.NOT_EMPLOYED]: []
});
const calculateRequiredIncomeDocsFromIncomeTypes = (incomeTypes) => {
    const requiredIncomeDocs = incomeTypes.reduce((acc, incomeType) => {
        const requiredDocs = exports.UNDERWRITING_REQUIREMENTS[incomeType].map(({ docType, min }) => ({
            docType,
            minRequired: min || 1
        }));
        return [...acc, ...requiredDocs];
    }, []);
    const uniqueRequiredIncomeDocs = requiredIncomeDocs.reduce((acc, { docType, minRequired }) => {
        const existingDocType = acc.find((doc) => doc.docType === docType);
        if (existingDocType) {
            existingDocType.minRequired = Math.max(existingDocType.minRequired, minRequired);
            return acc;
        }
        return [...acc, { docType, minRequired }];
    }, []);
    const orderedDocTypes = [
        exports.RENTAL_APP_INCOME_DOC_TYPES.PAY_STUB,
        exports.RENTAL_APP_INCOME_DOC_TYPES.OFFER_LETTER,
        exports.RENTAL_APP_INCOME_DOC_TYPES.BANK_STATEMENT,
        exports.RENTAL_APP_INCOME_DOC_TYPES.TAX_RETURN,
        exports.RENTAL_APP_INCOME_DOC_TYPES.SOCIAL_SECURITY_AWARD_LETTER,
        exports.RENTAL_APP_INCOME_DOC_TYPES.ARTICLES_OF_ORGANIZATION
    ];
    const sortedUniqueRequiredIncomeDocs = uniqueRequiredIncomeDocs.sort((a, b) => {
        const aIndex = orderedDocTypes.indexOf(a.docType);
        const bIndex = orderedDocTypes.indexOf(b.docType);
        return aIndex - bIndex;
    });
    return sortedUniqueRequiredIncomeDocs;
};
exports.calculateRequiredIncomeDocsFromIncomeTypes = calculateRequiredIncomeDocsFromIncomeTypes;
const curriedTemplate = (documentName) => {
    return (numStatements) => `Upload your ${numStatements === 1 ? '' : number_to_words_1.default.toWords(numStatements) + ' '}most recent ${documentName}`;
};
exports.FINANCIAL_DOCS_INSTRUCTIONS = Object.freeze({
    [documents_1.DOCUMENT_TYPES.OFFER_LETTER]: {
        headerText: (numStatements) => `Upload ${numStatements === 1 ? 'a' : number_to_words_1.default.toWords(numStatements)} signed offer ${(0, pluralize_1.default)('letters', numStatements)}`,
        bodyText: 'Since you do not have pay stubs we need a signed offer letter with the following requirements:',
        listItems: [
            'Signed offer letter on company letterhead',
            'Confirmed start date',
            'Confirmed salary or hourly wage with expected hours per week'
        ]
    },
    [documents_1.DOCUMENT_TYPES.PAY_STUB]: {
        headerText: (numStatements) => curriedTemplate('pay stubs')(numStatements),
        bodyText: 'Upload paystubs showing your income for the past month from your current employer. If you get paid once every two weeks, you will need to upload two paystubs.',
        listItems: []
    },
    [documents_1.DOCUMENT_TYPES.BANK_STATEMENT]: {
        headerText: (numStatements) => `${`Upload your most recent ${number_to_words_1.default.toWords(numStatements)} ${(0, pluralize_1.default)('months', numStatements)} of personal bank statements`}`,
        bodyText: 'We need your recent bank statements in order to verify your income.',
        listItems: []
    },
    [documents_1.DOCUMENT_TYPES.TAX_RETURN]: {
        headerText: (numStatements) => curriedTemplate(`personal tax ${(0, pluralize_1.default)('returns', numStatements)}`)(numStatements),
        bodyText: 'As a self employed individual we need your recent tax return in order to verify your income.',
        listItems: []
    },
    [documents_1.DOCUMENT_TYPES.SOCIAL_SECURITY_AWARD_LETTER]: {
        headerText: (numStatements) => curriedTemplate(`Social Security award ${(0, pluralize_1.default)('letters', numStatements)}`)(numStatements),
        bodyText: 'We need your Social Security award letters in order to verify your income.',
        listItems: []
    },
    [documents_1.DOCUMENT_TYPES.PENSION]: {
        headerText: (numStatements) => curriedTemplate(`pension ${(0, pluralize_1.default)('statement', numStatements)}`)(numStatements),
        bodyText: 'We need your pension statements in order to verify your income.',
        listItems: []
    },
    [documents_1.DOCUMENT_TYPES.OTHER_FINANCIAL_DOCUMENT]: {
        headerText: (numStatements) => `Upload the requested ${numStatements === 1 ? '' : number_to_words_1.default.toWords(numStatements) + ' '}${(0, pluralize_1.default)('documents', numStatements)}`,
        bodyText: 'We need these documents in order to verify your income.',
        listItems: []
    },
    [documents_1.DOCUMENT_TYPES.ARTICLES_OF_ORGANIZATION]: {
        headerText: (numDocs) => curriedTemplate(`articles of organization ${(0, pluralize_1.default)('documents', numDocs)}`)(numDocs),
        bodyText: 'As a self employed individual we need your articles of organization for your business in order to verify your income.',
        listItems: []
    }
});
